import React, { useRef } from "react";
import moment from "moment";
import { useTranslation } from "react-i18next";

const money = (n) =>
  Number(n || 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const buildCss = (width) => `
  /* ── print page ─────────────────────────── */
  @page {
    size: ${width} auto;
    margin: 1mm 0mm !important;
  }

  *, *::before, *::after { box-sizing: border-box; }
  html, body { margin: 0; padding: 0; background: #fff; }

  /* ── receipt shell ─────────────────────── */
  .rcpt {
    width: ${width};
    max-width: ${width};
    min-width: ${width};
    margin: 0 auto;
    background: #fff;
    color: #000;
    font-size: 14px;
    font-weight: 600;
    line-height: 1;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
  .rcpt p { margin: 0; padding: 0; }

  /* ── print override: fill the paper width ── */
  @media print {
    .rcpt {
      width: 100% !important;
      max-width: 100% !important;
      min-width: 0 !important;
      padding: 0 !important;
    }
  }

  /* ── header ──────────────────────────────── */
  .rcpt-head { text-align: center; margin-bottom: 2px; }
  .rcpt-isp-name {
    font-size: 16px;
    font-weight: 700;
    line-height: 1.1;
    word-break: break-word;
  }

  /* ── dividers ────────────────────────────── */
  .rcpt-rule {
    border: 0;
    border-top: 1px dashed #000;
    margin: 4px 0;
    font-weight: 600;
  }
  .rcpt-rule-double {
    border: 0;
    border-top: 3px double #000;
    margin: 4px 0;
  }

  /* ── PAYMENT RECEIPT title ───────────────── */
  .rcpt-title {
    text-align: center;
    font-size: 16px;
    font-weight: 700;
    letter-spacing: 1px;
    padding: 1px 0;
    text-transform: uppercase;
  }

  /* ── generic key / value row ─────────────── */
  .rcpt-row {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    gap: 6px;
    padding: 1.5px 0;
    width: 100%;
  }
  .rcpt-row .k {
    flex: 0 0 auto;
    font-weight: 500;
    white-space: nowrap;
  }
  .rcpt-row .v {
    flex: 1;
    text-align: right;
    font-weight: 600;
    word-break: break-word;
  }

  /* bold rows — totals / paid / next due */
  .rcpt-row.bold { padding: 2px 0; }
  .rcpt-row.bold .k,
  .rcpt-row.bold .v {
    font-weight: 700;
    font-size: 14px;
  }

  /* ── account status block ─────────────────── */
  .rcpt-status-block {
    margin: 4px 0;
    padding: 4px 0;
    border-top: 1px solid #000;
    border-bottom: 1px solid #000;
  }
  .rcpt-status-block .ttl {
    text-align: center;
    font-size: 14px;
    font-weight: 700;
    letter-spacing: 0.5px;
    padding-bottom: 3px;
  }

  /* ── signature line ──────────────────────── */
  .rcpt-sign {
    display: flex;
    justify-content: space-between;
    gap: 12px;
    margin-top: 14px;
  }
  .rcpt-sign div {
    flex: 1;
    padding-top: 3px;
    border-top: 1px solid #000;
    text-align: center;
    font-size: 13px;
    font-weight: 600;
  }

  /* ── duplicate stamp ─────────────────────── */
  .rcpt-dup {
    margin-top: 6px;
    padding: 3px 0;
    text-align: center;
    font-size: 13px;
    font-weight: 700;
    letter-spacing: 3px;
    border: 1.5px solid #000;
  }

  /* ── footer ──────────────────────────────── */
  .rcpt-foot { text-align: center; margin-top: 4px; }
  .rcpt-foot .proof  { font-size: 12px; font-weight: 500; }
  .rcpt-foot .thanks { font-size: 12px; font-weight: 700; margin-top: 2px; }
  .rcpt-foot .extra  { font-size: 12px; font-weight: 500; margin-top: 2px; }

  /* ── cut line ────────────────────────────── */
  .rcpt-cut {
    margin-top: 8px;
    text-align: center;
    font-size: 14px;
    font-weight: 600;
    letter-spacing: 2px;
    overflow: hidden;
    white-space: nowrap;
  }

  /* ── 58 mm override ──────────────────────── */
  .rcpt.is-58 { font-size: 10px; }
  .rcpt.is-58 .rcpt-isp-name { font-size: 12px; }
  .rcpt.is-58 .rcpt-title    { font-size: 10px; }
  .rcpt.is-58 .rcpt-row.bold .k,
  .rcpt.is-58 .rcpt-row.bold .v { font-size: 10px; }
`;

const Row = ({ k, v, bold }) => (
  <div className={`rcpt-row${bold ? " bold" : ""}`}>
    <span className="k">{k}</span>
    <span className="v">{v}</span>
  </div>
);

const computeNextDue = (invoiceData) => {
  const rawCycle =
    invoiceData?.billingCycle || invoiceData?.customer?.billingCycle;
  if (!rawCycle) return null;

  const cycleMoment = moment(rawCycle);
  const isPaid = Number(invoiceData?.due ?? 0) <= 0;
  const isActive =
    (
      invoiceData?.customer?.status ||
      invoiceData?.status ||
      ""
    ).toLowerCase() === "active";

  return isPaid && isActive ? cycleMoment.clone().add(1, "month") : cycleMoment;
};

const CustomerInvoicePOSPrint = React.forwardRef((props, ref) => {
  const {
    invoiceData,
    ispOwnerData,
    paperWidth = "80mm",
    currency = "৳",
    footerNote,
  } = props;

  const { t } = useTranslation();
  const is58 = paperWidth === "58mm";

  /* ── unwrap data ── */
  const customer = invoiceData?.customer || {};
  const amount = Number(invoiceData?.amount || 0);
  const discount = Number(invoiceData?.discount || 0);
  const due = Number(invoiceData?.due ?? 0);
  const isPaid = due <= 0;

  const createdAt = invoiceData?.createdAt
    ? moment(invoiceData.createdAt)
    : moment();

  const previousDue = Number(
    invoiceData?.previousDue ?? invoiceData?.prevState?.due ?? 0,
  );
  const monthlyBill = Number(
    invoiceData?.monthlyBill ?? invoiceData?.billAmount ?? 0,
  );
  const totalPayable = previousDue + monthlyBill - discount;

  const collector =
    invoiceData?.collectedBy?.name || invoiceData?.collector?.name;
  const method = t(invoiceData?.medium || "Cash");

  const nextDueMoment = computeNextDue(invoiceData);

  return (
    <div ref={ref} className={`rcpt${is58 ? " is-58" : ""}`}>
      <style>{buildCss(paperWidth)}</style>

      {/* ════════════ HEADER ════════════ */}
      <div className="rcpt-head">
        <p className="rcpt-isp-name">
          {ispOwnerData?.company || ispOwnerData?.name || "[ ISP NAME ]"}
        </p>
      </div>

      <div className="rcpt-rule-double" />
      <div className="rcpt-title">{t("PAYMENT RECEIPT")}</div>
      <div className="rcpt-rule-double" />

      {/* ════════════ DATE / COLLECTOR ════════════ */}
      <Row k={`${t("Date")} :`} v={createdAt.format("DD MMM YYYY, hh:mm A")} />
      {collector && <Row k={`${t("Collected")} :`} v={collector} />}

      <hr className="rcpt-rule" />

      {/* ════════════ CUSTOMER ════════════ */}
      <Row k={`${t("User ID")} :`} v={customer?.customerId || "—"} />
      <Row k={`${t("Name")} :`} v={customer?.name || "—"} />
      {customer?.mobile && <Row k={`${t("Mobile")} :`} v={customer.mobile} />}
      {customer?.address && (
        <Row k={`${t("Address")} :`} v={customer.address} />
      )}

      <hr className="rcpt-rule" />

      {/* ════════════ BILL BREAKDOWN ════════════ */}
      <Row k={t("Previous Due")} v={`${currency} ${money(previousDue)}`} />
      <Row k="Month" v={invoiceData?.month} />
      <Row k={`${t("Monthly Bill")}`} v={`${currency} ${money(monthlyBill)}`} />
      <Row k={t("Discount")} v={`- ${currency} ${money(discount)}`} />

      <hr className="rcpt-rule" />

      <Row
        k={t("Total Payable")}
        v={`${currency} ${money(totalPayable > 0 ? totalPayable : amount)}`}
        bold
      />

      <hr className="rcpt-rule" />

      {/* ════════════ PAYMENT ════════════ */}
      <Row
        k={`${t("PAID")} (${method})`}
        v={`${currency} ${money(amount)}`}
        bold
      />
      <Row
        k={t("Remaining Due")}
        v={`${currency} ${money(due < 0 ? 0 : due)}`}
      />

      {/* ════════════ ACCOUNT STATUS ════════════ */}
      {isPaid ? (
        <div className="rcpt-status-block">
          <p className="ttl">*** {t("ACCOUNT RENEWED")} ***</p>
          <Row k={`${t("Status")}     :`} v={t("ACTIVE")} />
          <Row k={`${t("Valid From")} :`} v={createdAt.format("DD MMM YYYY")} />
          {nextDueMoment && (
            <Row
              k={`${t("Next Due")}   :`}
              v={nextDueMoment.format("DD MMM YYYY")}
              bold
            />
          )}
        </div>
      ) : (
        <div className="rcpt-status-block">
          <p className="ttl">*** {t("PARTIAL PAYMENT")} ***</p>
          <Row k={`${t("Status")} :`} v={t("DUE")} />
          {nextDueMoment && (
            <Row
              k={`${t("Clear Due Before")} :`}
              v={nextDueMoment.format("DD MMM YYYY")}
              bold
            />
          )}
        </div>
      )}

      <div className="rcpt-foot">
        <p className="proof">{t("Keep this slip as payment proof.")}</p>
        <p className="thanks">{t("Thank you for staying with us!")}</p>
        {footerNote && <p className="extra">{footerNote}</p>}
      </div>
    </div>
  );
});

CustomerInvoicePOSPrint.displayName = "CustomerInvoicePOSPrint";
export default CustomerInvoicePOSPrint;

export const CustomerInvoicePOSPrintWrapper = ({
  invoiceData,
  ispOwnerData,
  paperWidth = "80mm",
  currency = "৳",
  showSignature = false,
  footerNote,
  isDuplicate = false,
  qrValue,
}) => {
  const printRef = useRef();
  const { t } = useTranslation();

  const handlePrint = () => {
    if (!printRef.current) return;

    const iframe = document.createElement("iframe");
    iframe.setAttribute("aria-hidden", "true");
    iframe.style.cssText =
      "position:fixed;right:0;bottom:0;width:0;height:0;border:0;visibility:hidden;";
    document.body.appendChild(iframe);

    const cleanup = () => {
      setTimeout(() => {
        if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
      }, 500);
    };

    const win = iframe.contentWindow;
    const doc = win.document;
    doc.open();
    doc.write(`<!DOCTYPE html>
<html>
  <head>
    <meta charset="utf-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1" />
    <title>${t("payment receipt")}</title>
    <style>
      @page { size: ${paperWidth} auto; }
      *, *::before, *::after { box-sizing: border-box; }
      html, body { margin: 0; padding: 0; background: #fff; }
      /* make sure receipt fills paper width on print */
      .rcpt {
        width: 100% !important;
        max-width: 100% !important;
        min-width: 0 !important;
        padding: 0 !important;
      }
    </style>
  </head>
  <body>${printRef.current.outerHTML}</body>
</html>`);
    doc.close();

    win.onafterprint = cleanup;

    setTimeout(() => {
      win.focus();
      win.print();
      setTimeout(cleanup, 60_000);
    }, 150);
  };

  return (
    <div>
      <button
        type="button"
        onClick={handlePrint}
        className="btn btn-primary mb-2"
      >
        {t("print receipt")}
      </button>

      {/* ── Screen preview ── */}
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          background: "#d0d0d0",
          borderRadius: "6px",
          overflowX: "auto",
          lineHeight: 1,
        }}
      >
        <CustomerInvoicePOSPrint
          ref={printRef}
          invoiceData={invoiceData}
          ispOwnerData={ispOwnerData}
          paperWidth={paperWidth}
          currency={currency}
          showSignature={showSignature}
          footerNote={footerNote}
          isDuplicate={isDuplicate}
          qrValue={qrValue}
        />
      </div>
    </div>
  );
};
